import { createSlice } from "@reduxjs/toolkit";
import { getState } from "@bd-stores/store";

/**
 * @type {BD_StoreGameProgress}
 */
const initialState = {
    players: [],
    finalTeamAnswers: {},
    teamAnswers: {},
    roomStatus: {},
    totalAttempts: {},
    timeSpend: {},
    isRoomTimeout: {},
    roomScores: {},
    logos: {},
    roomCurrentScreen: {},
    allRoomCompleted: false,
    isFinished: false,
    isMiddleState: false,
    isTeamConnectorFinished: false,
    currentRoomStartedAt: null,
    currentRoomShouldFinisheddAt: null,
    currentRoomFinisheddAt: null,
    currentChallengeIndex: 0,
    leader: 0,
    isLeader: false,
    isChangingLeader: false,
    newLeaderAttributes: {
        newLeader: 0,
        isConfirmed: true,
    },
    leaderHistory: [],
    leaderChanged: {},
    teamConnectorReady: false,
    currentOpenedChat: false,
    emailPhishingAction: false,
};

const gameProgressSlice = createSlice({
    name: "gameProgress",
    initialState,
    reducers: {
        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['players']>} action
         */
        setPlayers: (state, action) => ({
            ...state,
            players: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['finalTeamAnswers']>} action
         */
        setFinalTeamAnswers: (state, action) => ({
            ...state,
            finalTeamAnswers: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['teamAnswers']>} action
         */
        setTeamAnswers: (state, action) => ({
            ...state,
            teamAnswers: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['roomStatus']>} action
         */
        setRoomStatus: (state, action) => ({
            ...state,
            roomStatus: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['allRoomCompleted']>} action
         */
        setAllRoomCompleted: (state, action) => ({
            ...state,
            allRoomCompleted: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['isTeamConnectorFinished']>} action
         */
        setTeamConnectorFinished: (state, action) => ({
            ...state,
            isTeamConnectorFinished: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['isFinished']>} action
         */
        setFinished: (state, action) => ({
            ...state,
            isFinished: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['isMiddleState']>} action
         */
        setIsMiddleState: (state, action) => ({
            ...state,
            isMiddleState: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['totalAttempts']>} action
         */
        setTotalAttempts: (state, action) => ({
            ...state,
            totalAttempts: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['timeSpend']>} action
         */
        setTimeSpend: (state, action) => ({
            ...state,
            timeSpend: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['isRoomTimeout']>} action
         */
        setIsRoomTimeout: (state, action) => ({
            ...state,
            isRoomTimeout: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['roomScores']>} action
         */
        setRoomScores: (state, action) => ({
            ...state,
            roomScores: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['logos']>} action
         */
        setLogos: (state, action) => ({
            ...state,
            logos: action.payload,
        }),

        setCurrentRoomScreen: (state, action) => ({
            ...state,
            roomCurrentScreen: action.payload,
        }),

        setCurrentRoomStartedAt: (state, action) => ({
            ...state,
            currentRoomStartedAt: action.payload,
        }),

        setCurrentRoomShouldFinishedAt: (state, action) => ({
            ...state,
            currentRoomShouldFinisheddAt: action.payload,
        }),

        setCurrentRoomFinishedAt: (state, action) => ({
            ...state,
            currentRoomFinisheddAt: action.payload,
        }),

        setCurrentChallengeIndex: (state, action) => ({
            ...state,
            currentChallengeIndex: action.payload,
        }),

        setLeader: (state, action) => ({
            ...state,
            leader: action.payload,
        }),

        setIsLeader: (state, action) => ({
            ...state,
            isLeader: action.payload,
        }),

        setIsChangingLeader: (state, action) => ({
            ...state,
            isChangingLeader: action.payload,
        }),

        setNewLeaderAttributes: (state, action) => ({
            ...state,
            newLeaderAttributes: action.payload,
        }),

        setLeaderHistory: (state, action) => ({
            ...state,
            leaderHistory: action.payload,
        }),

        setLeaderChanged: (state, action) => ({
            ...state,
            leaderChanged: action.payload,
        }),

        setTeamConnectorReady: (state, action) => ({
            ...state,
            teamConnectorReady: action.payload,
        }),

        setCurrentOpenedChat: (state, action) => ({
            ...state,
            currentOpenedChat: action.payload
        }),

        setEmailPhishingAction: (state, action) => ({
            ...state,
            emailPhishingAction: action.payload
        }),
    },
});

export const getRoomStatus = (roomId) => {
    const roomStatus = getState().gameProgress.roomStatus;
    return roomStatus?.[roomId] || "is-status-open";
};

export const getCurrentRoomScreen = (roomId) => {
    const roomScreen = getState().gameProgress.roomCurrentScreen;
    return roomScreen?.[roomId] || false;
};

export const getFinalTeamAnswers = (roomId) => {
    const finalTeamAnswers = getState().gameProgress.finalTeamAnswers;
    return finalTeamAnswers?.[roomId] || false;
};

export const getTeamAnswers = (roomId) => {
    const teamAnswers = getState().gameProgress.teamAnswers;
    return teamAnswers?.[roomId] || false;
};

export const getPlayers = () => {
    const players = getState().gameProgress.players;
    return players ?? [];
};

export const getUserFromId = (userId) => {
    const players = getState().gameProgress.players;
    const user = players.find((p) => p.userId === userId);
    return user;
};

export const getTotalAttempt = (roomId) => {
    const roomAttempt = getState().gameProgress.totalAttempts;
    return roomAttempt?.[roomId] || 0;
};

export const getRoomScore = (roomId) => {
    const roomScore = getState().gameProgress.roomScores;
    return roomScore?.[roomId] || 0;
};

export const getRoomLogo = (roomId) => {
    const logos = getState().gameProgress.logos;
    return logos?.[roomId] || false;
};

export const getLogos = () => {
    return getState().gameProgress.logos;
};

export const getIsRoomTimeOut = (roomId) => {
    const roomTimeout = getState().gameProgress.isRoomTimeout;
    return roomTimeout?.[roomId] || false;
};

export const getLeader = () => {
    return getState().gameProgress.leader;
};

export const isLeader = () => {
    return getState().gameProgress.isLeader;
};

export const isChangingLeader = () => {
    return getState().gameProgress.isChangingLeader;
};

export const getNewLeaderAttributes = () => {
    return getState().gameProgress.newLeaderAttributes;
};

export const getLeaderHistory = () => {
    return getState().gameProgress.leaderHistory;
};

export const getRoomStatuses = () => {
    return getState().gameProgress.roomStatus;
};

export const getCurrentChallengeIndex = () => {
    return getState().gameProgress.currentChallengeIndex;
};

export const getLeaderChanged = () => {
    return getState().gameProgress.leaderChanged;
};

export const getCurrentOpenedChat = () => {
    return getState().gameProgress.currentOpenedChat;
};

export const getEmailPhishingAction = () => {
    return getState().gameProgress.emailPhishingAction;
}

export const {
    setPlayers,
    setTeamAnswers,
    setFinalTeamAnswers,
    setRoomStatus,
    setAllRoomCompleted,
    setFinished,
    setIsMiddleState,
    setTeamConnectorFinished,
    setTotalAttempts,
    setTimeSpend,
    setIsRoomTimeout,
    setRoomScores,
    setLogos,
    setCurrentRoomScreen,
    setCurrentRoomStartedAt,
    setCurrentRoomShouldFinishedAt,
    setCurrentRoomFinishedAt,
    setCurrentChallengeIndex,
    setLeader,
    setIsLeader,
    setIsChangingLeader,
    setNewLeaderAttributes,
    setLeaderHistory,
    setLeaderChanged,
    setTeamConnectorReady,
    setCurrentOpenedChat,
    setEmailPhishingAction
} = gameProgressSlice.actions;
export const gameProgress = gameProgressSlice.reducer;
